import { useMutation } from '@apollo/client';
import { Col } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import { InputComponent } from 'components/common/Input';
import { INPUT_TYPE } from 'components/common/Input/types';
import Table from 'components/Table';
import { MobileTableType } from 'components/Table/types';
import { formatDateTimeForDisplay } from 'constants/days';
import deviceSizes from 'constants/deviceSizes';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { CREATE_PAYMENT, DELETE_PAYMENT, UPDATE_PAYMENT } from 'services/graphql/mutations/payment';
import { IPayment, PaymentMethod } from 'types/payment';
import { defaultNotification } from 'utils';
import IntlMessages from 'utils/IntlMessages';
import * as Yup from 'yup';

import EditablePaymentField from './EditablePaymentField';
import { IInvoicePaymentProps } from './types';

const PaymentDetail: React.FC<IInvoicePaymentProps> = ({ invoice, refetch }) => {
  const [selectedPayment, setSelectedPayment] = useState<{ type: 'edit' | 'delete'; payment: IPayment }>();
  const paymentTableFormRef = useRef<any>();
  const createPaymentSchemaObject = {
    reference: Yup.string().notRequired(),
    method: Yup.string().required('Required'),
    amount: Yup.number()
      .max((invoice?.balance || 0) + (selectedPayment?.payment.amount || 0), "Can't exceed the balance")
      .required('Required'),
  };

  const [isAddPayment, setAddPaymentState] = useState(false);
  const [createPayment, { loading: createPaymentMutationLoading }] = useMutation(CREATE_PAYMENT);
  const [updatePayment, { loading: updatePaymentMutationLoading }] = useMutation(UPDATE_PAYMENT);
  const [deletePayment, { loading: deletePaymentMutationLoading }] = useMutation(DELETE_PAYMENT);

  const onPaymentActionButtonClick = (payment: IPayment, type: 'edit' | 'delete') => {
    setSelectedPayment({ payment, type });
  };

  const onPaymentActionButtonCancelClick = () => {
    setSelectedPayment(undefined);
  };

  const onDeletePaymentConfirm = async () => {
    if (!selectedPayment) return;
    try {
      await deletePayment({
        variables: {
          id: selectedPayment.payment.id,
        },
      });
      defaultNotification({ success: 'Delete payment successfully!' });
      setSelectedPayment(undefined);
      refetch?.();
    } catch (err) {
      console.log('Error', err);
      defaultNotification({ failure: 'Delete payment failed! Please try again.' });
    }
  };

  const invoiceTableColumn: ColumnsType<any> = [
    { dataIndex: 'date', title: 'Date' },
    {
      dataIndex: 'method',
      title: 'Method',
      render: (method: string, record: any) => (
        <EditablePaymentField
          type={INPUT_TYPE.SELECT}
          inputOptions={Object.keys(PaymentMethod).map((paymentType) => ({
            value: paymentType,
            label: paymentType,
          }))}
          editable={selectedPayment?.type === 'edit' && record?.key === selectedPayment.payment.id}
          name='method'>
          <span className=''>{method}</span>
        </EditablePaymentField>
      ),
      width: '20%',
    },
    {
      dataIndex: 'reference',
      title: 'Reference Number',
      render: (reference: string, record: any) => (
        <EditablePaymentField
          editable={selectedPayment?.type === 'edit' && record?.key === selectedPayment.payment.id}
          name='reference'>
          <span className='gx-font-weight-medium'>{reference}</span>
        </EditablePaymentField>
      ),
      width: '25%',
    },
    {
      dataIndex: 'amount',
      title: 'Amount',
      render: (amount: any, record: any) => (
        <EditablePaymentField
          editable={selectedPayment?.type === 'edit' && record?.key === selectedPayment.payment.id}
          name='amount'
          inputSettings={{ prefix: '$' }}>
          <span style={{ color: '#038fde' }}>${amount}</span>
        </EditablePaymentField>
      ),
      width: '15%',
    },
    {
      dataIndex: 'actions',
      title: '',
      width: '20%',
      render: (_, record: any) => {
        const isThisPaymentSelected = !!selectedPayment && selectedPayment.payment.id === record.key;
        return (
          <div className='payment-action-buttons-container'>
            {!isThisPaymentSelected && (
              <>
                <ButtonWithSpinner
                  className='gx-mx-0'
                  bgColor='transparent'
                  bgColorOnHover='transparent'
                  buttonType='default'
                  color='grey'
                  border='none'
                  width='auto'
                  colorOnHover='#038fde'
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onPaymentActionButtonClick(record?.payment || '', 'edit');
                  }}>
                  <i className='icon icon-edit' />
                </ButtonWithSpinner>
                <ButtonWithSpinner
                  bgColor='transparent'
                  bgColorOnHover='transparent'
                  buttonType='default'
                  border='none'
                  width='auto'
                  color='grey'
                  colorOnHover='red'
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onPaymentActionButtonClick(record?.payment || '', 'delete');
                  }}>
                  <i className='icon icon-trash' />
                </ButtonWithSpinner>
              </>
            )}
            {/* if edit payment is clicked */}
            {isThisPaymentSelected && selectedPayment?.type === 'edit' && (
              <>
                <ButtonWithSpinner
                  width='90px'
                  buttonType='primary'
                  // the submit function is from handleSubmit of edit payment form formik ref's props
                  onClick={paymentTableFormRef.current?.handleSubmit}
                  className='gx-m-0'
                  isLoading={updatePaymentMutationLoading}>
                  Save
                </ButtonWithSpinner>
                <ButtonWithSpinner
                  width='90px'
                  buttonType='text'
                  onClick={onPaymentActionButtonCancelClick}
                  color='red'
                  className='gx-m-0'
                  spinnerColor='lightgrey'>
                  Cancel
                </ButtonWithSpinner>
              </>
            )}
            {/* if delete payment is clicked */}
            {!!isThisPaymentSelected && selectedPayment?.type === 'delete' && (
              <>
                <div className='gx-font-weight-medium'>Confirm delete?</div>
                <ButtonWithSpinner
                  width='auto'
                  color='#038fde'
                  buttonType='text'
                  isLoading={deletePaymentMutationLoading}
                  onClick={onDeletePaymentConfirm}
                  className='gx-m-0 gx-ml-2'>
                  Yes
                </ButtonWithSpinner>
                <ButtonWithSpinner
                  width='auto'
                  buttonType='text'
                  onClick={onPaymentActionButtonCancelClick}
                  color='red'
                  className='gx-m-0'
                  spinnerColor='lightgrey'>
                  No
                </ButtonWithSpinner>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const dataSource = invoice?.payments?.map((payment) => ({
    key: payment.id,
    date: moment(payment.createdAt).format(formatDateTimeForDisplay('|')),
    method: payment.method,
    reference: payment.reference,
    amount: payment.amount,
    payment,
  }));

  const onPaymentClick = () => {
    setAddPaymentState(true);
  };

  const onCancelClick = () => {
    setAddPaymentState(false);
  };

  const onSavePayment = async (value: any, formikProps?: any) => {
    const paymentValue = {
      ...value,
      amount: parseFloat(value.amount),
      invoice: invoice?.id || '',
    };
    try {
      await createPayment({
        variables: {
          doc: paymentValue,
        },
      });
      defaultNotification({ success: 'Create payment successfully!' });
      onCancelClick();
      formikProps?.resetForm?.();
      refetch?.();
    } catch (err) {
      console.log('Error', err);
      defaultNotification({ failure: 'Create payment failed! Please try again.' });
    }
  };

  const onEditPaymentConfirm = async (value: any, formikProps?: any) => {
    if (!selectedPayment) return;
    const paymentValue = {
      ...value,
      amount: parseFloat(value.amount),
      invoice: invoice?.id || '',
    };
    try {
      await updatePayment({
        variables: {
          updatedDoc: paymentValue,
          filter: { id: selectedPayment?.payment.id },
        },
      });
      defaultNotification({ success: 'Update payment successfully!' });
      onCancelClick();
      formikProps?.resetForm?.();
      refetch?.();
      setSelectedPayment(undefined);
    } catch (err) {
      console.log('Error', err);
      defaultNotification({ failure: 'Update payment failed! Please try again.' });
    }
  };

  const createPaymentSchema = Yup.object(createPaymentSchemaObject);

  return (
    <div>
      <div className='payment-detail-title'>
        <span className='gx-mr-4 gx-mb-2 gx-font-weight-medium'>PAYMENT</span>
        <div className='payment-detail-title-side'>
          <ButtonWithSpinner
            style={{ display: isAddPayment || invoice?.balance === 0 ? 'none' : undefined }}
            onClick={onPaymentClick}
            width='auto'>
            + Payment
          </ButtonWithSpinner>
          <Formik
            initialValues={{ amount: '', method: '', reference: '' }}
            name='basic'
            onSubmit={onSavePayment}
            validationSchema={createPaymentSchema}
            enableReinitialize
            validateOnBlur>
            <Form style={{ display: !isAddPayment ? 'none' : undefined }}>
              <Col span={24} lg={16} className='payment-form gx-px-0'>
                <Col span={24} sm={8} className='gx-mb-2'>
                  <InputComponent name='reference' placeholder='Reference' />
                </Col>
                <Col span={24} sm={8} className='gx-mb-2'>
                  <InputComponent
                    name='method'
                    placeholder='Select Type'
                    type={INPUT_TYPE.SELECT}
                    inputOptions={Object.keys(PaymentMethod).map((paymentType) => ({
                      value: paymentType,
                      label: paymentType,
                    }))}
                  />
                </Col>
                <Col span={24} sm={8} className='gx-mb-2'>
                  <InputComponent name='amount' placeholder='Amount' />
                </Col>
              </Col>
              <Col span={24} lg={8} className='gx-pl-0'>
                <ButtonWithSpinner isLoading={createPaymentMutationLoading} className='gx-mb-2' htmlType='submit'>
                  Create
                </ButtonWithSpinner>
                <ButtonWithSpinner buttonType='default' className='gx-mb-2' onClick={onCancelClick}>
                  <IntlMessages id='appModule.cancel' />
                </ButtonWithSpinner>
              </Col>
            </Form>
          </Formik>
        </div>
      </div>
      <Formik
        onSubmit={onEditPaymentConfirm}
        validationSchema={createPaymentSchema}
        initialValues={{
          method: selectedPayment?.payment.method,
          reference: selectedPayment?.payment.reference,
          amount: selectedPayment?.payment.amount,
        }}
        enableReinitialize
        innerRef={paymentTableFormRef}>
        <div className='p-4 sm:p-0'>
          <Table
            dataSource={dataSource}
            columns={invoiceTableColumn}
            pagination={false}
            widthThreshold={deviceSizes.numbers.sm}
            mobileDesignType={MobileTableType.RESPONSIVE_TABLE}
            responsiveFullBordered
          />
        </div>
      </Formik>
    </div>
  );
};

export default React.memo(PaymentDetail);
